import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Setting {
	key: string
	value: any
	description?: string
	category?: string
	isSystem?: boolean
}

export interface CreateSettingData {
	key: string
	value: any
	description?: string
	category?: string
}

export interface UpdateSettingData {
	value: any
	description?: string
}

interface SettingsResponse {
	data: Setting[]
}

export const useSettings = (category?: string) => {
	return useQuery({
		queryKey: ['settings', category],
		queryFn: () => {
			const params = category ? { category } : {}
			return apiClient.get<SettingsResponse>('/settings', params)
		},
	})
}

export const useSetting = (key: string) => {
	return useQuery({
		queryKey: ['setting', key],
		queryFn: () => apiClient.get<Setting>(`/settings/${key}`),
		enabled: !!key,
	})
}

export const useCreateSetting = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateSettingData) => apiClient.post<Setting>('/settings', data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['settings'] })
		},
	})
}

export const useUpdateSetting = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ key, data }: { key: string; data: UpdateSettingData }) =>
			apiClient.patch<Setting>(`/settings/${key}`, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['settings'] })
		},
	})
}

export const useBulkUpdateSettings = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (settings: Array<{ key: string; value: any }>) => {
			return Promise.all(
				settings.map(({ key, value }) => apiClient.post('/settings', { key, value })),
			)
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['settings'] })
		},
	})
}
